import { FunctionComponent, useCallback, useState } from 'react'
import { Alert, Flex, getTheme, Input, Text } from '@conexasaude/conexa-hero'
import Modal from '@/components/Modal'
import { MinorAccessModalProps, MinorAccessRequestData } from './types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { minorAccessRequestSchema } from '../../validations/minorAccessRequestSchema.validation'
import { TITLE } from './constants'
import {
  useCheckLegalGuardianData,
  useCheckMinorsTermAcceptance,
  useCheckTermAcceptance,
  useGetLegalGuardianRelationship,
  useSendMinorAccessRequest,
} from '../../queries'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { AnalyticEvent } from '@/modules/Activation/types/en-us'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { SEND_TO } from '@/modules/Signup/constants'
import { getDigitsFromString } from '@/utils/getDigitsFromString'
import { sha256Hash } from '@conexasaude/analytics'
import AutocompleteControl from '@/components/Form/HookFormControls/AutocompleteControl'
import { Description } from './components/Description'
import { GuardianFormStatus } from './components/Description/types'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useMinorAccessRequestStore } from '../../store/MinorAccessRequest.store'
import { RenderIf } from '@/components/RenderIf'
import { useNavigation } from '@/hooks/useNavigation'
import { useAuthContext } from '../../contexts/AuthContext'
import themeServices from '@/theme/theme.services'
import service from '@/modules/Auth/auth-services'
import { setRefreshToken, setToken } from '@/utils/token'
import { getCache, setCache } from '@/utils/cache'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { IUser } from '../../auth-types'

const MinorAccessRequestModal: FunctionComponent<MinorAccessModalProps> = ({
  modalId,
}) => {
  const { logAnalyticsEvent } = useFirebaseContext()
  const theme = getTheme()
  const [showAlertAcceptanceTermPending, setShowAlertAcceptanceTermPending] =
    useState<boolean>(false)
  const { mutateAsync: sendMinorAccessRequest, isLoading } =
    useSendMinorAccessRequest()
  const { minorAccessRequestData } = useMinorAccessRequestStore()

  const { showModal, closeModal } = useModalContext()
  const { data: legalGuardianData, refetch } = useCheckLegalGuardianData(
    minorAccessRequestData?.idPatient,
  )
  const { isLoading: isLoadingCheckTerm } = useCheckTermAcceptance()
  const { mutateAsync: checkMinorsTermAcceptance } =
    useCheckMinorsTermAcceptance()

  const { data: legalGuardianRelationship } = useGetLegalGuardianRelationship()
  const navigate = useNavigation()
  const { signIn, choiceUser, setUserClinic } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()

  const guardianFormStatus: GuardianFormStatus =
    legalGuardianData?.sentTimestamp || legalGuardianData?.acceptanceTermId
      ? 'submitted'
      : 'pending'
  const colorTitle =
    guardianFormStatus === 'submitted'
      ? theme.colors.brand.primary[300]
      : theme.colors.neutral[500]

  const secondaryActionLabel = isLoadingCheckTerm
    ? 'Verificando liberação ...'
    : 'Meu responsável já liberou'

  const form = useForm<MinorAccessRequestData>({
    resolver: zodResolver(minorAccessRequestSchema),
    mode: 'onSubmit',
    defaultValues: {
      guardianEmail: '',
      guardianName: '',
      guardianCellphone: '',
      guardianKinship: { key: '', value: '' },
    },
  })
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
  } = form

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      const normalizedCPF = getDigitsFromString(minorAccessRequestData?.cpf)
      const hashedCPF = sha256Hash(normalizedCPF)
      const eventName = eventDefinitions?.eventName
      logAnalyticsEvent(eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        options: {
          sendTo: SEND_TO,
        },
        patient_type: minorAccessRequestData?.holder,
        clinic_id: minorAccessRequestData?.idClinic,
        account_type: minorAccessRequestData?.clinicProduct,
        ...(hashedCPF && { cnx_user_id: hashedCPF }),
        ...(hashedCPF && { user_id: hashedCPF }),
      })
    },
    [
      logAnalyticsEvent,
      minorAccessRequestData?.clinicProduct,
      minorAccessRequestData?.cpf,
      minorAccessRequestData?.holder,
      minorAccessRequestData?.idClinic,
    ],
  )

  const handlePrimaryAction = async data => {
    try {
      const token = minorAccessRequestData?.token
      const isMinorTermAccepted = await checkMinorsTermAcceptance(token)
      if (isMinorTermAccepted) setUser()
      else sendRequest(data)
    } catch (error) {
      showErrorToast(error)
    }
  }

  const sendRequest = async data => {
    settingUpAnalyticsEvent({
      eventName: 'at_patient_responsible_form_modal',
      interaction: 'inform_responsible',
    })
    const payloadData: MinorAccessRequestData = {
      ...data,
      idPatient: minorAccessRequestData?.idPatient,
      idClinic: minorAccessRequestData?.idClinic,
      patientName: minorAccessRequestData?.patientName,
      token: minorAccessRequestData?.token,
      refreshToken: minorAccessRequestData?.refreshToken,
    }
    try {
      await sendMinorAccessRequest(payloadData)
      closeModal()
      showModal('ACCESS_REQUEST_SEND_MODAL', {
        persistent: true,
      })
      refetch()
    } catch (error) {
      showErrorToast(error)
    }
  }

  const checkTermAcceptance = async () => {
    try {
      const token = minorAccessRequestData?.token
      const isMinorTermAccepted = await checkMinorsTermAcceptance(token)
      setShowAlertAcceptanceTermPending(!isMinorTermAccepted)
      if (isMinorTermAccepted) setUser()
    } catch (error) {
      showErrorToast(error)
    }
  }

  const setUser = useCallback(async () => {
    try {
      const users = getCache<IUser[]>('users')
      const user = users?.find(
        user =>
          user?.usuario?.paciente?.id === minorAccessRequestData?.idPatient,
      )

      const { token, refreshToken } = user
      const { flagAcessou, clinica, id } = user?.usuario ?? {}

      const [userClinic, clinicColors, ip] = await Promise.all([
        service.getClinic(token),
        themeServices.getClinicColorsById(clinica.id),
        service.getUserIP().catch(() => {
          return ''
        }),
      ])

      const { clinic, flags } = userClinic

      if (!flagAcessou) await service.validateFirstAccess(token)

      signIn(users)
      choiceUser(id, ip)
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', id)
      setThemeColors(clinicColors)
      setUserClinic(userClinic)
      setSpecialities(clinic)

      if (flags?.PermiteGeolocalizacao) {
        navigator.geolocation.getCurrentPosition(position => {
          const {
            accuracy,
            altitude,
            altitudeAccuracy,
            heading,
            latitude,
            longitude,
            speed,
          } = position.coords
          service.sendGeolocation({
            accuracy,
            altitude,
            altitudeAccuracy,
            heading,
            latitude,
            longitude,
            speed,
          })
        })
      }

      navigate('/')
    } catch (err) {
      showErrorToast(err)
    } finally {
      closeModal()
    }
  }, [
    signIn,
    choiceUser,
    setThemeColors,
    setUserClinic,
    setSpecialities,
    navigate,
    minorAccessRequestData?.idPatient,
    closeModal,
  ])

  return (
    <Modal
      id={modalId ?? 'GUARDIAN_ACCESS_REQUEST_MODAL'}
      customHeader={
        <Text variant="headingSmall" fontWeight={600} color={colorTitle}>
          {TITLE[guardianFormStatus]}
        </Text>
      }
      primaryAction={handleSubmit(handlePrimaryAction)}
      busySecondaryAction={isLoadingCheckTerm}
      secondaryAction={checkTermAcceptance}
      {...(guardianFormStatus === 'submitted' && {
        secondaryActionLabel: secondaryActionLabel,
      })}
      primaryActionLabel="Solicitar"
      busyPrimaryAction={isLoading}
      size={480}
    >
      <Flex direction="column" p="xs" mb="xs" gap="xs">
        <Description guardianFormStatus={guardianFormStatus} />
        <form onSubmit={handleSubmit(sendRequest)}>
          <Flex direction="column" gap="sm" mt="xs" mb="xxs">
            <Text variant="subheading">Dados do responsável legal</Text>
            <Input
              {...register('guardianName')}
              label="Nome do responsável*"
              placeholder="Nome completo do responsável"
              name="guardianName"
              maxLength={50}
              fullWidth
              error={errors.guardianName?.message?.toString()}
            />
            <AutocompleteControl
              label="Parentesco*"
              placeholder="Selecione uma opção"
              getOptionLabel={option => option.value}
              name="guardianKinship"
              control={control}
              optionLabel="value"
              options={legalGuardianRelationship || []}
              fullWidth
              error={errors?.guardianKinship?.message?.toString()}
            />
            <Input
              {...register('guardianEmail')}
              label="E-mail*"
              name="guardianEmail"
              error={errors.guardianEmail?.message?.toString()}
              fullWidth
              placeholder="Insira o e-mail do responsável"
            />
            <Input
              {...register('guardianCellphone')}
              error={errors?.guardianCellphone?.message}
              label="Telefone*"
              name="guardianCellphone"
              placeholder="Insira aqui o telefone do responsável"
              mask="(99) 99999-9999"
              fullWidth
            />
            <RenderIf condition={showAlertAcceptanceTermPending}>
              <Alert
                title="Seu acesso ainda não foi liberado"
                variant="warning"
                text="Verifique com a sua pessoa responsável se a liberação já foi realizada e tente novamente."
              />
            </RenderIf>
          </Flex>
        </form>
      </Flex>
    </Modal>
  )
}

export { MinorAccessRequestModal }
